import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    LayoutModule,
    SharedUiModule,
    TemplateDrawerComponent,
} from '@pf/shared-ui';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import {
    ItemDocumentsFacade,
    ItemDto,
    ItemFacade,
} from '@pf/platform-item-catalog/domain';
import { findCustomField } from '@pf/shared/util-platform';
import { asNumberSafe } from '@pf/shared-utility';
import { of, switchMap } from 'rxjs';

export interface CpwItemInfoDrawerData {
    itemId: string;
}

/**
 * Reusable item-information drawer. Opened from the item thumbnail
 * ({@link CpwItemImageComponent}) on any operation screen — shows the enlarged
 * image plus the item's identity, UoM, dimensions, weight, and description.
 *
 * Mirrors {@link CpwEquipmentInfoDrawerComponent}. Loads its own image (rather
 * than embedding cpw-item-image) to avoid a circular component dependency.
 */
@Component({
    selector: 'cpw-item-info-drawer',
    imports: [SharedUiModule, LayoutModule, NzSkeletonModule],
    templateUrl: './cpw-item-info-drawer.component.html',
    styleUrl: './cpw-item-info-drawer.component.scss',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpwItemInfoDrawerComponent
    implements TemplateDrawerComponent<CpwItemInfoDrawerData>
{
    private readonly itemFacade = inject(ItemFacade);
    private readonly itemDocumentsFacade = inject(ItemDocumentsFacade);
    private readonly destroyRef = inject(DestroyRef);
    readonly data: CpwItemInfoDrawerData = inject(NZ_DRAWER_DATA);
    drawerRef = inject(NzDrawerRef);

    readonly item = signal<ItemDto | null>(null);
    /** undefined = still loading, null = item has no image, string = image URL. */
    readonly imageUrl = signal<string | null | undefined>(undefined);

    constructor() {
        const id = this.data.itemId;
        if (!id) {
            this.imageUrl.set(null);
            return;
        }
        this.itemFacade
            .getById$(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(item => {
                if (item) {
                    this.item.set(item);
                    this.loadImage(item.id);
                }
            });
    }

    readonly uom = computed(
        () => findCustomField(this.item()?.customFields, 'UoM')?.value || 'EA'
    );

    readonly dimensions = computed<string | null>(() => {
        const d = this.item()?.dimensions;
        const l = asNumberSafe(d?.length?.inches);
        const w = asNumberSafe(d?.width?.inches);
        const h = asNumberSafe(d?.height?.inches);
        if (!l && !w && !h) {
            return null;
        }
        return `${l} × ${w} × ${h} in`;
    });

    readonly weight = computed<string | null>(() => {
        const pounds = asNumberSafe(this.item()?.weight?.pounds);
        return pounds ? `${pounds} lb` : null;
    });

    private loadImage(id: string): void {
        this.imageUrl.set(undefined);
        this.itemFacade
            .getDefaultItemImage$(id)
            .pipe(
                switchMap(file =>
                    file?.id
                        ? this.itemDocumentsFacade.previewUrl$(id, file.id)
                        : of(null)
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(url => this.imageUrl.set(url ?? null));
    }
}
